<template>
  <div class="file-uploader">
    <div class="h-100 w-100 align-items-center justify-content-center d-flex gap-2">

      <input type="file" @change="handleFileChange" id="file-input" style="display: none;" multiple />

      <div class="col-2 h-100">
        
          <div class="boton-adjuntar">
            <button class="btn btn-secondary " @click="selectFile">
            <i class="bi bi-paperclip"></i>
            </button>
          </div>
    
        <button v-if="showUploadButton" class="btn btn-success btn-sm" :disabled="files.length === 0" @click="uploadAllFiles">
        <i class="bi bi-upload"></i> Subir Todo
        </button>
    
      </div>

      <div class="col-10 h-100">
        <div class="list-uploader h-100">
        <!-- Lista de archivos seleccionados -->
          <div v-if="files.length > 0" class="files-list">
            <div v-for="(fileItem, index) in files" :key="index" class="file-item">
              <span class="file-name">
                <i class="bi bi-file-earmark"></i>
                {{ fileItem.file.name }}
                <span v-if="fileItem.status === 'uploading'" class="status uploading">
                <i class="bi bi-arrow-clockwise spinner"></i> Subiendo...
                </span>
                <span v-else-if="fileItem.status === 'success'" class="status success">
                <i class="bi bi-check-circle"></i> Subido
                </span>
                <span v-else-if="fileItem.status === 'error'" class="status error">
                <i class="bi bi-x-circle"></i> Error
                </span>
                <i class="bi bi-x-circle remove-icon" @click="removeFile(index)" title="Eliminar archivo"></i>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import API from '../services';

const props = defineProps<{
  showUploadButton?: boolean;
  destPath?: string;
}>();

interface FileItem {
  file: File;
  status: 'pending' | 'uploading' | 'success' | 'error';
}

const files = ref<FileItem[]>([]);

const emit = defineEmits<{
  (event: 'upload-success', response: any, file: File): void;
  (event: 'upload-error', error: any, file: File): void;
  (event: 'all-uploads-complete', results: { file: File; success: boolean; response?: any; error?: any }[]): void;
}>();

// Usar valor por defecto de true si no se proporciona
const showUploadButton = props.showUploadButton ?? true;

// Debug: verificar que se reciben las props correctamente
console.log('FileUploader inicializado con props:', props);

function selectFile() {
  const input = document.getElementById('file-input') as HTMLInputElement;
  input.click();
}

function handleFileChange(event: Event) {
  const target = event.target as HTMLInputElement;
  if (target.files && target.files.length > 0) {
    // Agregar todos los archivos seleccionados
    const newFiles = Array.from(target.files).map(file => ({
      file,
      status: 'pending' as const
    }));
    files.value.push(...newFiles);
  }
}

function removeFile(index: number) {
  files.value.splice(index, 1);
  // Limpiar el input file para permitir seleccionar el mismo archivo nuevamente
  const input = document.getElementById('file-input') as HTMLInputElement;
  if (input) {
    input.value = '';
  }
}

async function uploadSingleFile(fileItem: FileItem): Promise<{ success: boolean; response?: any; error?: any }> {
  if (!fileItem.file) return { success: false };

  fileItem.status = 'uploading';

  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = async () => {
      const base64Content = (reader.result as string).split(',')[1];
      
      console.log('Props destPath:', props.destPath);
      console.log('Props completas:', JSON.stringify(props));
      console.log('Tipo de destPath:', typeof props.destPath);
      
      const payload: any = {
        filename: fileItem.file.name,
        contentType: fileItem.file.type,
        fileContent: base64Content
      };

      // Agregar destPath si existe - verificación explícita
      console.log('¿destPath existe?', props.destPath !== undefined);
      console.log('¿destPath tiene valor?', !!props.destPath);
      
      if (props.destPath !== undefined && props.destPath !== null && props.destPath !== '') {
        payload.destPath = props.destPath;
        console.log('destPath agregado al payload:', props.destPath);
      } else {
        console.log('destPath NO se agregó - valor:', props.destPath);
      }

      console.log('Payload completo antes de enviar:', JSON.stringify(payload));
      console.log('Keys del payload:', Object.keys(payload));

      try {
        const response = await API.uploadFileService(payload);
        console.log('Archivo subido exitosamente:', response);
        fileItem.status = 'success';
        emit('upload-success', response, fileItem.file);
        resolve({ success: true, response });
      } catch (error) {
        console.error('Error al subir el archivo:', error);
        fileItem.status = 'error';
        emit('upload-error', error, fileItem.file);
        resolve({ success: false, error });
      }
    };

    reader.readAsDataURL(fileItem.file);
  });
}

async function uploadAllFiles() {
  console.log('Subiendo todos los archivos...');
  const pendingFiles = files.value.filter(f => f.status === 'pending');
  const results: { file: File; success: boolean; response?: any; error?: any }[] = [];

  for (const fileItem of pendingFiles) {
    const result = await uploadSingleFile(fileItem);
    results.push({
      file: fileItem.file,
      ...result
    });
  }

  emit('all-uploads-complete', results);
}

// Función legacy para compatibilidad (sube el primer archivo)
async function uploadSelectedFile() {
  if (files.value.length > 0) {
    await uploadSingleFile(files.value[0]);
  }
}

// Exponer las funciones para uso desde el componente padre
defineExpose({
  uploadSelectedFile,
  uploadAllFiles,
  files,
  showUploadButton
});
</script>

<style scoped>
.file-uploader {
  display: flex;
  align-items: center;
  gap: 10px;
  height: 100px;
  width: 100%;

}

.files-list {
  display: flex;
  flex-direction: column;
  gap: 5px;
  margin-top: 10px;
  width: 100%;
}

.file-item {
  display: flex;
  align-items: center;
}

.file-name {
  background-color: rgba(255, 255, 255, 0.3);
  border-radius: 5px;
  color: #ffffff;
  font-size: 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 3px;
}

.status {
  font-size: 11px;
  padding: 2px 6px;
  border-radius: 4px;
  margin-left: auto;
}

.boton-adjuntar .btn {
 background-color: rgb(91, 161, 223) ;
 width: 100%;
 aspect-ratio: 1 / 1;

}

.boton-adjuntar .btn:hover {
 background-color: rgb(212, 235, 255) ;
}

.boton-adjuntar .btn:active {
 background-color: rgb(54, 98, 137) ;
}

.status.uploading {
  background-color: #17a2b8;
  color: white;
  border-radius: 5px;
  font-weight: bolder;
}

.status.success {
  background-color: #28a745;
  color: white;
  border-radius: 5px;
  font-weight: bolder;
}

.status.error {
  background-color: #dc3545;
  color: white;
  border-radius: 5px;
  font-weight: bolder;
}

.spinner {
  animation: spin 1s linear infinite;
}

.container-uploader {
  border: 2px dashed white;
  border-radius: 5px;
  background-color: rgba(255, 255, 255, 0.1);
  padding: 10px;
  margin: 0;
}

.list-uploader {
    margin-left: 0;
    padding-left: 10px;
    padding-right: 10px;
    border-radius: 5px;
    width: 100%;
    max-height: 100%; /* Altura máxima del contenedor */
    overflow-y: auto; /* Habilitar scroll vertical */
    border: 1px solid #ccc; /* Borde para delimitar el área */
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.remove-icon {
  background-color: #dc3545;
  border-radius: 100%;
  width: 21px;
  color: white;
  cursor: pointer;
  font-size: 14px;
  justify-self: flex-end;
  font-weight: bold;
}


.bi-x-circle::before {
  margin-left: 3.5px;
}
.remove-icon:hover {
  color: #c82333;
}

.bi-upload {
  font-size: 30px;
  color: #ffffff;
}
</style>